// src/components/company/CompanySidebar.jsx
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useBranding } from "../../contexts/BrandingContext";
import { isIOS } from "../../lib/platform";
import {
  Banknote,
  DollarSign,
  LayoutDashboard,
  FolderKanban,
  Timer,
  Plane,
  Users,
  Building2,
  LineChart,
  ReceiptIcon,
  Flag,
  FileCheck,
  ShieldCheck,
  X,
  CreditCard,
  AlertTriangle,
  Calendar,
  Activity,
  CalendarDays,
  FolderOpen,
  Archive,
  Inbox,
  Megaphone,
  // Notices. Megaphone is already this sidebar's "My Postings", and two
  // items sharing one glyph is worse than a slightly less apt glyph.
  BellRing,
  LayoutGrid,
  Palette,
  Package,
  ChevronDown,
  MessageSquare,
  ListChecks,
} from "lucide-react";

const COLLAPSE_KEY = "sift_company_sidebar_collapsed_v1";
// All section labels. On first visit we collapse everything except "Overview"
// so the company sidebar opens tidy rather than fully expanded.
const GROUP_LABELS = [
  "Overview", "Contracts & Projects", "Time & Travel", "Compliance",
  "Marketplace", "Team", "Finance", "Account",
];

/**
 * Navigation item with permission check + active styling.
 */
const Item = ({ to, icon: Icon, label, onNavigate, visible = true, dataTour }) => {
  const branding = useBranding();
  const brandColor = branding.branded ? branding.primary_color : null;

  if (!visible) return null;

  return (
    <NavLink
      to={to}
      data-tour={dataTour}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors border-l-[3px] ${
          isActive
            ? brandColor
              ? "text-white font-semibold border-transparent"
              : "bg-brand-tint text-brand font-semibold border-brand"
            : "border-transparent text-secondary hover:bg-sunken hover:text-ink"
        }`
      }
      style={({ isActive }) =>
        isActive && brandColor ? { backgroundColor: brandColor, borderColor: brandColor } : undefined
      }
      onClick={onNavigate}
    >
      {({ isActive }) => (
        <>
          <Icon size={16} className={isActive ? (brandColor ? "text-white" : "text-brand") : "text-tertiary"} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
};

/**
 * Collapsible section. Header toggles; collapse state persists.
 */
const Section = ({ label, collapsed, onToggle, children, visible = true }) => {
  if (!visible) return null;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="w-full flex items-center justify-between px-2 mb-2 hover:opacity-80 transition-opacity"
      >
        <span className="text-[0.8125rem] font-medium text-tertiary">{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-tertiary transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`} />
      </button>
      {!collapsed && <div className="flex flex-col gap-1">{children}</div>}
    </div>
  );
};

export default function CompanySidebar({ org, onNavigate, onClose }) {
  const branding = useBranding();
  const isCreamLabel = branding.branded;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) {}
    // First visit: collapse all but "Overview".
    return new Set(GROUP_LABELS.slice(1));
  });
  const toggleGroup = (label) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(label) ? next.delete(label) : next.add(label);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch (_) {}
    return next;
  });
  const isClosed = (label) => collapsed.has(label);

  // Extract permissions from org object (populated by MeAPIView)
  const perms = org || {};

  // Quick permission checks - these come from the API now
  const canViewTeamPage = perms.can_view_team_page || false;
  const canViewOrgManagement = perms.can_view_org_management || false;
  const canViewIncomeStatement = perms.can_view_income_statement || false;
  const canViewStripeSettings = perms.can_view_stripe_settings || false;
  const canViewFinance = perms.can_view_finance || false;
  const canViewFraudAlerts = perms.can_view_fraud_alerts || false;
  const canViewTalentSearch = perms.can_view_talent_search !== false; // Default true for most roles
  const canDraftContracts = perms.can_draft_contracts !== false; // Default true - everyone can draft

  // Role info
  const role = perms.role;
  const roleDisplay = perms.role_display || role;
  const isOrgWide = perms.is_org_wide || false;

  // Check if user has any admin-level access
  const hasAdminAccess = canViewTeamPage || canViewOrgManagement;
  const hasFinanceAccess = canViewIncomeStatement || canViewStripeSettings || canViewFinance;

  return (
    <aside
      className="border border-line rounded-2xl p-4 h-full overflow-y-auto"
      style={{ backgroundColor: isCreamLabel ? branding.accent_color : "#FCFAF5" }}
    >
      {/* Header with org name and role badge */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className={
            isCreamLabel && branding.logo_font_style === "uppercase_bold"
              ? "text-lg font-extrabold uppercase tracking-[0.2em] text-white"
              : isCreamLabel && branding.logo_font_style === "italic_serif"
                ? "text-lg font-semibold italic font-serif text-white"
                : isCreamLabel
                  ? "text-lg font-semibold text-white"
                  : "text-lg font-semibold text-ink"
          }>{perms.name || "Company"}</div>
          {roleDisplay && (
            <div className={`text-xs mt-0.5 ${isCreamLabel ? "text-white/60" : "text-tertiary"}`}>
              {roleDisplay}
              {perms.department_name && ` • ${perms.department_name}`}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-2 hover:bg-sunken rounded-lg transition-colors text-ink"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      <div className="space-y-4 mt-4">
        {/* OVERVIEW */}
        <Section label="Overview" collapsed={isClosed("Overview")} onToggle={() => toggleGroup("Overview")}>
          <Item to="/company/dashboard" icon={LayoutDashboard} label="Dashboard" onNavigate={onNavigate} />
          <Item to="/messages" icon={MessageSquare} label="Messages" onNavigate={onNavigate} />
          <Item to="/company/calendar" icon={Calendar} label="Calendar" onNavigate={onNavigate} />
        </Section>

        {/* CONTRACTS & PROJECTS */}
        <Section label="Contracts & projects" collapsed={isClosed("Contracts & Projects")} onToggle={() => toggleGroup("Contracts & Projects")}>
          <Item to="/company/contracts" icon={FolderKanban} label="Contracts" onNavigate={onNavigate} />
          <Item to="/company/documents" icon={FileCheck} label="Documents to sign" onNavigate={onNavigate} />
          <Item to="/company/notices" icon={BellRing} label="Notices" onNavigate={onNavigate} />
          {/* Internal Assignments: your own people's work, W-2 or not (2026-10-08).
              Was gated on w2_features_enabled, which hid it from 1099-only
              agencies that need it for clock-in and pay runs. */}
          <Item
            to="/company/internal-projects"
            icon={FolderOpen}
            label="Internal assignments"
            onNavigate={onNavigate}
            visible={hasAdminAccess || role === "Manager"}
          />
          <Item to="/company/milestones" icon={Flag} label="Milestones" onNavigate={onNavigate} />
          {/* Product Catalog — saved quote line-items. Shown to orgs that can send quotes. */}
          <Item
            to="/company/catalog"
            icon={Package}
            label="Product catalog"
            onNavigate={onNavigate}
            visible={!!perms.feature_flags?.one_time_customers}
          />
          <Item to="/company/purchase-orders" icon={FileCheck} label="Purchase orders" onNavigate={onNavigate} />
          <Item
            to="/company/workforce"
            icon={Activity}
            label="Workforce Center"
            onNavigate={onNavigate}
            visible={hasAdminAccess || role === "Manager"}
          />
          <Item
            to="/company/contracts/archive"
            icon={Archive}
            label="Contract archive"
            onNavigate={onNavigate}
            visible={hasAdminAccess || role === "Manager"}
          />
        </Section>

        {/* TIME & TRAVEL */}
        <Section label="Time & travel" collapsed={isClosed("Time & Travel")} onToggle={() => toggleGroup("Time & Travel")}>
          <Item to="/company/timecards" icon={Timer} label="Timecards" onNavigate={onNavigate} dataTour="company-timecards" />
          {/* Pay many people in one payment. Owners and Admins: it moves money. */}
          <Item to="/company/pay-runs" icon={Banknote} label="Pay runs" onNavigate={onNavigate}
            visible={role === "Owner" || role === "Admin"} />
          <Item to="/company/time-off" icon={CalendarDays} label="Time off" onNavigate={onNavigate} />
          <Item to="/company/travel" icon={Plane} label="Travel Center" onNavigate={onNavigate} />
        </Section>

        {/* COMPLIANCE */}
        <Section label="Compliance" collapsed={isClosed("Compliance")} onToggle={() => toggleGroup("Compliance")}>
          <Item to="/company/credentials" icon={ShieldCheck} label="Credential Center" onNavigate={onNavigate} dataTour="company-credentials" />
        </Section>

        {/* MARKETPLACE */}
        <Section label="Marketplace" collapsed={isClosed("Marketplace")} onToggle={() => toggleGroup("Marketplace")}>
          <Item to="/browse?show=person" icon={LayoutGrid} label="Browse people" onNavigate={onNavigate} dataTour="company-browse-postings" />
          <Item to="/browse?show=service" icon={Users} label="Browse services" onNavigate={onNavigate} dataTour="company-browse-profiles" />
          <Item to="/company/inbound-requests" icon={Inbox} label="Inbound requests" onNavigate={onNavigate} dataTour="company-inbound-requests" />
          <Item to="/company/postings" icon={Megaphone} label="My postings" onNavigate={onNavigate} dataTour="company-postings" />
          <Item to="/company/question-sets" icon={ListChecks} label="Question sets" onNavigate={onNavigate} />
          <Item to="/company/storefront" icon={Palette} label="Storefront editor" onNavigate={onNavigate} dataTour="company-storefront" />
        </Section>

        {/* TEAM - Owner/Admin only */}
        <Section label="Team" visible={hasAdminAccess} collapsed={isClosed("Team")} onToggle={() => toggleGroup("Team")}>
          <Item to="/company/team" icon={Users} label="Team" onNavigate={onNavigate} visible={canViewTeamPage} dataTour="company-team" />
          <Item to="/company/org-management" icon={Building2} label="Org management" onNavigate={onNavigate} visible={canViewOrgManagement} />
        </Section>

        {/* FINANCE - Owner/Admin or finance permission */}
        <Section label="Finance" visible={hasFinanceAccess} collapsed={isClosed("Finance")} onToggle={() => toggleGroup("Finance")}>
          <Item to="/company/finance" icon={ReceiptIcon} label="Reporting Center" onNavigate={onNavigate} visible={canViewFinance || isOrgWide} dataTour="company-finance" />
          <Item to="/company/finance/income" icon={LineChart} label="Income statement" onNavigate={onNavigate} visible={canViewIncomeStatement && perms.revenue_tracking_enabled !== false} />
        </Section>

        {/* ACCOUNT - Owner/Admin only */}
        <Section label="Account" visible={canViewStripeSettings || isOrgWide} collapsed={isClosed("Account")} onToggle={() => toggleGroup("Account")}>
          {/* Billing UI is web-only per App Store guideline 3.1.1 */}
          {!isIOS() && (
            <Item to="/company/billing" icon={CreditCard} label="Billing & Stripe" onNavigate={onNavigate} visible={canViewStripeSettings} dataTour="company-billing" />
          )}
          <Item to="/company/agreements" icon={FileCheck} label="Documents from Sift" onNavigate={onNavigate} visible={role === "Owner"} />
          <Item to="/company/partner-program" icon={DollarSign} label="Partner program" onNavigate={onNavigate} visible={role === "Owner" && !!perms.is_partner_org} />
          <Item to="/company/fraud" icon={AlertTriangle} label="Fraud alerts" onNavigate={onNavigate} visible={canViewFraudAlerts} />
        </Section>
      </div>

      {/* Subscription warning for non-subscribed orgs (Owner/Admin only).
          Hidden on iOS per App Store guideline 3.1.1. */}
      {isOrgWide && !perms.is_subscribed && !isIOS() && (
        <div className="mt-6 p-3 rounded-lg border" style={{ background: "#FBF3E2", borderColor: "#E8D9B0" }}>
          <div className="text-xs font-semibold" style={{ color: "#8A6320" }}>Subscription required</div>
          <div className="text-tertiary text-xs mt-1">
            Activate your subscription to unlock all features.
          </div>
          <NavLink
            to="/company/billing"
            className="text-xs text-brand hover:text-brand-hover mt-2 inline-block font-medium"
          >
            Set up billing →
          </NavLink>
        </div>
      )}
    </aside>
  );
}
